/**
 * The notification inbox — a popover under the bell from 700px, a sheet on a
 * phone (TRBUD-439; design v3 #6, v4 "The float level", v6 frame G).
 *
 * It was a modal `Dialog`. An inbox is something you glance at and leave, so it
 * now floats beside the bell at the float level instead of taking the screen,
 * and every control the dialog had is still here with its name: Unread only,
 * Mark all as read, Mark as read, Retry notifications, Previous and Next
 * notifications, Close notifications. Unread items carry the select fill and
 * the leading edge every selected thing shares.
 *
 * Focus moves in when it opens; Escape, the close button or a press outside
 * closes it and puts focus back on the bell. On a phone the sheet has a dimmed
 * backdrop that closes it too, and is modal there.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Envelope, NotificationsResponse } from "@truckbuddy/contract";
import { apiRequest } from "../lib/api";
import { useAppSession } from "../lib/shell";
import { useIsCompact } from "../lib/useIsCompact";
import { FOCUSABLE } from "./Dialog";
import { NavIcon } from "./NavIcons";

/** A same-origin, in-app path, or the home page: an item's link never leaves the app. */
function safeHref(href: string): string {
  return href.startsWith("/") && !href.startsWith("//") && !href.includes("\\") && ![...href].some((character) => character.charCodeAt(0) < 32) ? href : "/";
}

export function NotificationBell() {
  const { user, previewRole, roles, can, fleetTimeZone } = useAppSession();
  const client = useQueryClient();
  const compact = useIsCompact();
  const headingId = useId();
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);
  const bell = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);
  const key = ["notifications", user.userId, previewRole, roles, can];
  const query = useQuery({ queryKey: [...key, offset, unreadOnly],
    queryFn: async ({ signal }) => (await apiRequest<Envelope<NotificationsResponse>>(`/notifications?offset=${offset}&limit=20&unreadOnly=${unreadOnly}`, { signal })).data,
    refetchInterval: 30_000, refetchOnWindowFocus: true });
  const mark = useMutation({ mutationFn: (id: string | null) => apiRequest<{ updated: number }>(id ? `/notifications/${encodeURIComponent(id)}/read` : "/notifications/read-all", { method: "POST", body: {} }),
    onSuccess: async () => { setOffset(0); await client.invalidateQueries({ queryKey: key }); } });
  const unread = query.data?.unreadCount ?? 0;

  function close(returnFocus = true) {
    setOpen(false);
    if (returnFocus) bell.current?.focus();
  }

  // Placed under the bell, in page coordinates, so it rides with the top bar.
  useLayoutEffect(() => {
    if (!open || compact) return;
    const place = () => {
      const box = bell.current?.getBoundingClientRect();
      if (!box) return;
      setAnchor({
        top: box.bottom + window.scrollY + 6,
        right: Math.max(8, document.documentElement.clientWidth - box.right),
      });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open, compact]);

  // Focus in on open; Escape and a press outside close it.
  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      if (event.key === "Tab" && compact) {
        const controls = panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
        const first = controls?.[0];
        const last = controls?.[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    const onPointerDown = (event: PointerEvent | MouseEvent) => {
      const target = event.target as Node | null;
      if (!target || panel.current?.contains(target) || bell.current?.contains(target)) return;
      close();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
    // Mount and unmount only: `close` reads refs, so a stale closure is harmless.
  }, [open, compact]);

  return <>
    <button ref={bell} type="button" className="app-topbar-icon notification-bell"
      aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
      aria-expanded={open} aria-haspopup="dialog"
      onClick={() => (open ? close() : setOpen(true))}>
      <NavIcon name="bell" />
      {unread > 0 && <span className="notification-badge" aria-hidden="true">{unread > 99 ? "99+" : unread}</span>}
    </button>
    {open && createPortal(<>
      {compact && <div className="notification-backdrop" aria-hidden="true" onClick={() => close()} />}
      <section ref={panel} className={`notification-panel ${compact ? "notification-sheet" : "notification-popover"}`}
        role="dialog" aria-labelledby={headingId} {...(compact ? { "aria-modal": true } : {})}
        style={!compact && anchor ? { top: anchor.top, right: anchor.right } : undefined}>
        <div className="notification-head">
          <h2 id={headingId}>Notifications</h2>
          <button type="button" className="app-topbar-icon" aria-label="Close notifications" title="Close notifications" onClick={() => close()}>
            <NavIcon name="close" />
          </button>
        </div>
        {query.isError && <p className="notification-note" role="alert">Could not load notifications. <button type="button" className="btn btn-sm" onClick={() => { void query.refetch(); }}>Retry notifications</button></p>}
        {mark.isError && <p className="notification-note" role="alert">Could not mark notifications as read. Please try again.</p>}
        {query.isPending && <p className="notification-note" role="status">Loading notifications…</p>}
        {query.data && <>
          <p className="notification-note text-muted">{unread} unread · New activity appears here as it happens.</p>
          {query.data.items.length === 0 ? <p className="notification-note">{unreadOnly ? "No unread notifications." : "No notifications yet."}</p> : <ul className="notification-list">
            {query.data.items.map((item) => <li key={item.id} className={item.readAt ? "" : "notification-unread"}>
              <Link to={safeHref(item.href)} onClick={() => close(false)}><strong>{item.title}</strong><span>{item.body}</span></Link>
              <time className="notification-when" dateTime={item.occurredAt}>{new Intl.DateTimeFormat("en-US", { timeZone: fleetTimeZone, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(item.occurredAt))}</time>
              <div className="notification-meta">
                {item.readAt ? <span>Read</span> : <button type="button" className="btn btn-sm" disabled={mark.isPending} onClick={() => mark.mutate(item.id)}>Mark as read</button>}
              </div>
            </li>)}
          </ul>}
          <div className="notification-controls">
            <button type="button" className="btn btn-sm" disabled={offset === 0 || query.isFetching} onClick={() => setOffset(Math.max(0, offset - 20))}>Previous notifications</button>
            <span>{query.data.total ? `${offset + 1}–${offset + query.data.items.length} of ${query.data.total}` : "0 notifications"}</span>
            <button type="button" className="btn btn-sm" disabled={!query.data.hasMore || query.isFetching} onClick={() => setOffset(offset + 20)}>Next notifications</button>
          </div>
        </>}
        <div className="notification-foot">
          <button type="button" className="btn btn-sm" disabled={!unread || mark.isPending} onClick={() => mark.mutate(null)}>Mark all as read</button>
          <label className="notification-filter"><input type="checkbox" checked={unreadOnly} onChange={(e) => { setUnreadOnly(e.target.checked); setOffset(0); }} /> Unread only</label>
        </div>
      </section>
    </>, document.body)}
  </>;
}
